import { InfoCircleOutlined } from "@ant-design/icons";
import { Carousel, Popover } from "antd";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { selectPortalSession } from "../../../../store/slice/portalAuthSlice";
import { Status } from "../../../newLayout/page/components/Status/Status";
import { LeftArrow, Minus, Plus, RightArrow, ShoppingCart } from "../../assets/icons";
import {
  usePortalCurrencySymbol,
  usePortalFormattedAmount,
  usePortalFormattedQuantity,
} from "../../hooks/usePortalFormattedAmount";
import { getStockStatus, isOutOfStock } from "../../utils/stockUtils";
import {
  AddButton,
  ArrowButton,
  CardActions,
  CardContainer,
  CardHeader,
  CarouselContainer,
  ImageWrapper,
  PriceSection,
  ProductDetails,
  ProductName,
  QuantitySelector,
  SkuAndCase,
  TierInfoButton,
  TierPopoverPanel,
  TierPopoverRow,
} from "./ProductCard.styles";

/**
 * Formats a catalogue quantity-tier range for display.
 *
 * @param {{start_quantity?: string|number|null, end_quantity?: string|number|null}} tier
 * @param {(qty: unknown) => string|null} formatQuantity
 * @returns {string}
 */
const formatTierRange = (tier, formatQuantity) => {
  const start = tier?.start_quantity != null ? Number(tier.start_quantity) : null;
  const end = tier?.end_quantity != null ? Number(tier.end_quantity) : null;
  if (start == null && end == null) return "Any qty";
  if (end == null) return `${formatQuantity(start)}+`;
  // End is inclusive (same as pricelist editor: next tier starts at end + 1).
  return `${formatQuantity(start)}–${formatQuantity(end)}`;
};

/**
 * Builds a stable key for deduping identical tier rows on the client.
 * @param {{start_quantity?: unknown, end_quantity?: unknown, sales_price?: unknown}} tier
 * @returns {string}
 */
const tierKey = tier =>
  `${tier?.start_quantity ?? ""}|${tier?.end_quantity ?? ""}|${tier?.sales_price ?? ""}`;

/**
 * Whether an ordered qty falls in a tier (matches backend exclusive-end rule).
 * @param {{start_quantity?: unknown, end_quantity?: unknown}} tier
 * @param {number} quantity
 * @returns {boolean}
 */
const tierMatchesQuantity = (tier, quantity) => {
  if (!Number.isFinite(quantity) || quantity <= 0) return false;
  const start = tier?.start_quantity != null ? Number(tier.start_quantity) : null;
  const end = tier?.end_quantity != null ? Number(tier.end_quantity) : null;
  if (start == null) return true;
  if (quantity < start) return false;
  if (end == null) return true;
  return quantity <= end;
};

/**
 * Pick sales_price for the given qty from unique tier rows.
 * @param {Array<{sales_price?: unknown}>} tiers
 * @param {number} quantity
 * @returns {number|null}
 */
const selectTierPrice = (tiers, quantity) => {
  for (const tier of tiers) {
    if (!tierMatchesQuantity(tier, quantity)) continue;
    const price = Number(tier.sales_price);
    if (Number.isFinite(price)) return price;
  }
  return null;
};

export const ProductCard = ({ product, onAddToCart, isAddingToCart }) => {
  const [quantity, setQuantity] = useState(1);
  const [quantityInput, setQuantityInput] = useState("1");
  const carouselRef = useRef(null);
  const formatAmount = usePortalFormattedAmount();
  const formatQuantity = usePortalFormattedQuantity();
  const currencySymbol = usePortalCurrencySymbol();

  const { subdomain } = useParams();
  const session = useSelector(state => selectPortalSession(state, subdomain));
  const { display_stock_counts, display_product_prices } = session?.portalConfig || {};

  const stockDisplayed = display_stock_counts === true;
  const pricesDisplayed = display_product_prices === true;

  const stockStatus = useMemo(
    () => getStockStatus(product.available_stock),
    [product.available_stock],
  );
  const outOfStock = useMemo(
    () => stockDisplayed && isOutOfStock(product.available_stock),
    [stockDisplayed, product.available_stock],
  );

  /** Deduped tier rows from the API (used for live price + popover). */
  const allTiers = useMemo(() => {
    const tiers = product.quantity_tiers;
    if (!Array.isArray(tiers)) return [];
    const seen = new Set();
    const unique = [];
    for (const tier of tiers) {
      if (tier?.sales_price == null) continue;
      const key = tierKey(tier);
      if (seen.has(key)) continue;
      seen.add(key);
      unique.push(tier);
    }
    return unique;
  }, [product.quantity_tiers]);

  /** Show the info popover only when there is real volume pricing. */
  const quantityTiers = allTiers.length > 1 ? allTiers : [];

  /**
   * Unit price for the quantity currently on the card.
   * Uses local tier math so +/- updates the price without another API call;
   * cart still re-resolves server-side on add.
   */
  const displayUnitPrice = useMemo(() => {
    if (allTiers.length > 0) {
      const tierPrice = selectTierPrice(allTiers, quantity);
      if (tierPrice != null) return tierPrice;
    }
    if (product.customer_price != null) return Number(product.customer_price);
    if (product.actual_price != null) return Number(product.actual_price);
    return null;
  }, [allTiers, quantity, product.customer_price, product.actual_price]);

  const hasValidCustomerPrice = displayUnitPrice != null && Number(displayUnitPrice) > 0;
  const priceBlocksAdd = pricesDisplayed && !hasValidCustomerPrice;

  const showStrikethrough =
    product.actual_price != null &&
    displayUnitPrice != null &&
    Number(product.actual_price) !== Number(displayUnitPrice);

  const tierPopoverContent = (
    <TierPopoverPanel>
      <div className="tier-popover-hint">
        Unit price updates with quantity. Cart uses the same breaks.
      </div>
      <div className="tier-popover-list" role="list" aria-label="Quantity pricing tiers">
        {quantityTiers.map(tier => {
          const active = tierMatchesQuantity(tier, quantity);
          return (
            <TierPopoverRow key={tierKey(tier)} $active={active} role="listitem">
              <span className="tier-range">{formatTierRange(tier, formatQuantity)}</span>
              <span className="tier-price">
                {currencySymbol}
                {formatAmount(tier.sales_price)}
              </span>
            </TierPopoverRow>
          );
        })}
      </div>
    </TierPopoverPanel>
  );

  // Keep the editable qty input label in sync with +/- and commit formatting.
  useEffect(() => {
    setQuantityInput(formatQuantity(quantity) ?? String(quantity));
  }, [quantity, formatQuantity]);

  // Sort images or filter to primary first if order/is_primary details exist
  const sortedImages = useMemo(() => {
    if (!product.images || product.images.length === 0) return [];
    return [...product.images].sort((a, b) => {
      if (a.is_primary && !b.is_primary) return -1;
      if (!a.is_primary && b.is_primary) return 1;
      return (a.order || 0) - (b.order || 0);
    });
  }, [product.images]);

  /**
   * Clamps a raw quantity to stock / min rules and commits state.
   * @param {number} nextQty
   */
  const commitQuantity = nextQty => {
    let qty = Number(nextQty);
    if (!Number.isFinite(qty) || qty < 1) {
      qty = 1;
    }
    if (
      stockDisplayed &&
      product.available_stock != null &&
      qty > Number(product.available_stock)
    ) {
      qty = Number(product.available_stock);
    }
    setQuantity(qty);
    setQuantityInput(formatQuantity(qty) ?? String(qty));
  };

  const handleDecrement = () => {
    if (quantity > 1) {
      commitQuantity(quantity - 1);
    }
  };

  const handleIncrement = () => {
    if (
      !stockDisplayed ||
      product.available_stock == null ||
      quantity < Number(product.available_stock)
    ) {
      commitQuantity(quantity + 1);
    }
  };

  const handleQuantityInputChange = event => {
    setQuantityInput(event.target.value);
  };

  const handleQuantityInputBlur = () => {
    const parsed = Number(String(quantityInput).replace(/,/g, ""));
    commitQuantity(parsed);
  };

  const handleQuantityKeyDown = event => {
    if (event.key === "Enter") {
      event.currentTarget.blur();
    }
  };

  const handleAdd = async () => {
    if (outOfStock || priceBlocksAdd) return;
    const parsed = Number(String(quantityInput).replace(/,/g, ""));
    let qty = Number.isFinite(parsed) && parsed >= 1 ? parsed : 1;
    if (
      stockDisplayed &&
      product.available_stock != null &&
      qty > Number(product.available_stock)
    ) {
      qty = Number(product.available_stock);
    }
    commitQuantity(qty);
    const success = await onAddToCart(product.id, qty);
    if (success) {
      commitQuantity(1);
    }
  };

  const isIncrementDisabled = stockDisplayed
    ? outOfStock ||
      (stockDisplayed &&
        product.available_stock != null &&
        quantity >= Number(product.available_stock))
    : false;
  const incrementTitle = outOfStock
    ? "Cannot increase quantity because this product is out of stock"
    : stockDisplayed &&
        product.available_stock != null &&
        quantity >= Number(product.available_stock)
      ? `Cannot increase because available stock is ${formatQuantity(product.available_stock)}`
      : "Increase quantity";

  return (
    <CardContainer>
      {display_stock_counts === true && (
        <CardHeader>
          <Status statusName={stockStatus.label} />
        </CardHeader>
      )}

      <CarouselContainer>
        {sortedImages.length > 1 && (
          <ArrowButton className="prev" onClick={() => carouselRef.current?.prev()}>
            <LeftArrow />
          </ArrowButton>
        )}

        <Carousel
          ref={carouselRef}
          dots={sortedImages.length > 1}
          dotPosition="bottom"
          className="product-carousel"
        >
          {sortedImages.length > 0 ? (
            sortedImages.map(img => (
              <div key={img.id}>
                <ImageWrapper>
                  <img src={img.image} alt={product.name} />
                </ImageWrapper>
              </div>
            ))
          ) : (
            <div>
              <ImageWrapper>
                <div style={{ color: "#aaa" }}>No Image Available</div>
              </ImageWrapper>
            </div>
          )}
        </Carousel>

        {sortedImages.length > 1 && (
          <ArrowButton className="next" onClick={() => carouselRef.current?.next()}>
            <RightArrow />
          </ArrowButton>
        )}
      </CarouselContainer>

      <ProductDetails>
        <SkuAndCase>
          <span className="sku">SKU {product.number || "N/A"}</span>
        </SkuAndCase>

        <ProductName>{product.name}</ProductName>

        <PriceSection>
          {!pricesDisplayed ? null : priceBlocksAdd ? (
            <span className="not-available">Not available</span>
          ) : (
            <div className="price-row">
              <span className="price">
                {currencySymbol}
                {formatAmount(displayUnitPrice)}
              </span>
              {quantityTiers.length > 0 && (
                <Popover
                  content={tierPopoverContent}
                  title="Quantity pricing"
                  trigger="click"
                  placement="bottomLeft"
                  overlayClassName="portal-tier-popover"
                >
                  <TierInfoButton
                    type="button"
                    aria-label="Show quantity pricing tiers"
                    title="Quantity pricing"
                  >
                    <InfoCircleOutlined />
                  </TierInfoButton>
                </Popover>
              )}
              {showStrikethrough && (
                <span className="original-price">
                  {currencySymbol}
                  {formatAmount(product.actual_price)}
                </span>
              )}
            </div>
          )}
        </PriceSection>
      </ProductDetails>

      <CardActions>
        <QuantitySelector>
          <button
            onClick={handleDecrement}
            disabled={outOfStock || quantity <= 1 || priceBlocksAdd}
            title={
              outOfStock
                ? "Cannot decrease quantity because this product is out of stock"
                : "Decrease quantity"
            }
            aria-label="Decrease quantity"
          >
            <Minus />
          </button>
          <input
            className="quantity-value"
            type="text"
            inputMode="decimal"
            value={quantityInput}
            onChange={handleQuantityInputChange}
            onBlur={handleQuantityInputBlur}
            onKeyDown={handleQuantityKeyDown}
            disabled={outOfStock || priceBlocksAdd}
            aria-label="Quantity"
          />
          <button
            onClick={handleIncrement}
            disabled={isIncrementDisabled || priceBlocksAdd}
            title={incrementTitle}
            aria-label="Increase quantity"
          >
            <Plus />
          </button>
        </QuantitySelector>

        <AddButton onClick={handleAdd} disabled={outOfStock || isAddingToCart || priceBlocksAdd}>
          <ShoppingCart />
          <span>{isAddingToCart ? "Adding..." : "Add"}</span>
        </AddButton>
      </CardActions>
    </CardContainer>
  );
};
